import { useEffect, useRef, useState } from "react";

interface DDXModalProps {
	className?: string;
	children: React.ReactElement;
	handleCloseModal: Function;
	showModal: boolean;
	ariaLabelledBy?: string;
	ariaModal?: boolean;
	ariaLabel?: string;
	forceInlineModal?: boolean;
}

export const DDXModal: React.FC<DDXModalProps> = ({
	className,
	children,
	handleCloseModal,
	showModal,
	ariaLabel,
	ariaLabelledBy,
	ariaModal,
	forceInlineModal = false
}) => {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const dialogBackdropRef = useRef<HTMLDivElement>(null);
	const [openModal, setOpenModal] = useState(showModal);

	useEffect(() => {
		setOpenModal(showModal);
	}, [showModal]);

	useEffect(() => {
		const dialog = dialogRef.current;
		const dialogBackdrop = dialogBackdropRef.current;

		const handleKeyDown = (event: KeyboardEvent): void => {
			const allowedKeys: Set<string> = new Set(["Escape"]);
			if (!allowedKeys.has(event.code) || !event.composed) return;
			event.preventDefault();
			handleCloseModal();
		};

		if (dialog && dialogBackdrop) {
			// This prevents the close button from being focused as soon as the dialog opens. Safari doesn't fall for blur trap so this is the easiest way to achieve this.
			dialog.querySelector(":last-child")?.setAttribute("tabIndex", "0");
			dialogBackdrop.addEventListener("click", () => handleCloseModal());
			window.addEventListener("keydown", handleKeyDown);

			if (openModal) {
				forceInlineModal ? dialog.show() : dialog.showModal();
			}
		}

		return () => {
			dialogBackdrop?.removeEventListener("click", () => handleCloseModal());
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [dialogRef, dialogBackdropRef, handleCloseModal, openModal, forceInlineModal]);

	return openModal ? (
		<dialog
			className={`ddx-modal ${className} ${openModal ? "" : "closed"}${forceInlineModal ? " ddx-modal-inline" : ""}`}
			ref={dialogRef}
			aria-labelledby={ariaLabelledBy}
			aria-modal={ariaModal}
			aria-label={ariaLabel}
		>
			<div className={`ddx-modal-backdrop ${className}-backdrop`} ref={dialogBackdropRef} />
			{children}
		</dialog>
	) : null;
};
